import type { RealtimeTransport } from './events';
import type { Member, Message } from './message';
import type { PeerStore } from './peer-store';
import { addMember, applyMessage, removeMember, syncMembers } from './peers';

/** Keeps a channel's peer store in step with its presence answer, joins, leaves and publications. */
export type MemberTracker = {
  /** Replaces the member list with the server's answer, keeping what was heard while it was in flight. */
  refresh(): Promise<void>;
  /** A member joined; true when it is someone else, who may need to be told what this tab said before. */
  join(member: Member): boolean;
  /** A member left. */
  leave(member: Member): void;
  /** A member published `message`. */
  heard(from: Member, message: Message): void;
};

/** Every event here is about someone else; this tab's own connection is never stored. */
export function createMemberTracker(transport: RealtimeTransport, channel: string, store: PeerStore): MemberTracker {
  // The presence answer is a snapshot from before it was sent: members who left meanwhile must
  // not come back with it, and members who joined or spoke meanwhile must not be dropped by it.
  const leftDuringFetch = new Set<string>();
  const heardDuringFetch = new Set<string>();
  const isSelf = (member: Member) => member.clientId === transport.clientId();

  return {
    async refresh() {
      leftDuringFetch.clear();
      heardDuringFetch.clear();
      const members = await transport.presence(channel).catch(() => null);
      if (members === null) return;
      const others = members.filter((m) => !isSelf(m) && !leftDuringFetch.has(m.clientId));
      store.update((current) => syncMembers(current, others, Date.now(), heardDuringFetch));
    },
    join(member) {
      if (isSelf(member)) return false;
      leftDuringFetch.delete(member.clientId);
      heardDuringFetch.add(member.clientId);
      store.update((current) => addMember(current, member, Date.now()));
      return true;
    },
    leave(member) {
      if (isSelf(member)) return;
      leftDuringFetch.add(member.clientId);
      heardDuringFetch.delete(member.clientId);
      store.update((current) => removeMember(current, member.clientId));
    },
    heard(from, message) {
      if (isSelf(from)) return;
      heardDuringFetch.add(from.clientId);
      store.update((current) => applyMessage(current, from, message, Date.now()));
    },
  };
}
